import React, { memo } from 'react'
import './arrow-component.scss'
import { fmtInt, pct, toPctStr } from './ship-store-helpers'
import { useEssentials } from 'hooks/utils/useEssentials'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { Tooltip } from '@design-systems/canvas-ui'
import { getColorClass } from 'utils/data'
import { ShipFromStoreData } from 'types/store-fulfillment'

type Props = {
    sfsData: ShipFromStoreData
}

export const ArrowComponent: React.FC<Props> = ({ sfsData }) => {
    const { t } = useEssentials()
    const { headings, unitsCompletedLabel } = TRANSLATION_KEYS.storeFulfillment

    const late = sfsData?.late_units ?? 0
    const carryover = sfsData?.carryover_units ?? 0
    const today = sfsData?.work_units ?? 0
    const completed = sfsData?.completed_units ?? 0
    const forecast = sfsData?.forecast_units ?? 0
    const infPercentage = sfsData?.inf?.inf_percent ?? 0

    const totalUnits =
        today > forecast
            ? late + carryover + today
            : late + carryover + forecast

    const remaining = Math.max(0, forecast - today)

    const latePct = pct(late, totalUnits)
    const carryPct = pct(carryover, totalUnits)
    const todayPct = pct(today, totalUnits)
    const completedPct = pct(completed, totalUnits)
    const remainingPct = pct(remaining, totalUnits)
    const forecastPct = pct(forecast, totalUnits)
    const forecastLeftPct = latePct + carryPct
    const tooltipLocation = completedPct > 70 ? 'bottom-right' : 'right'

    return (
        <div className="rwd">
            {/* Forecast (unit plan) line above the bar */}
            <div className="rwd__forecast">
                <span className="rwd__forecast-label">{`${t(headings.unitPlan)}: ${fmtInt(forecast)}`}</span>
                <span
                    className="rwd__forecast-line"
                    style={{
                        width: toPctStr(forecastPct),
                        left: toPctStr(forecastLeftPct),
                    }}
                />
            </div>
            {/* Stacked bar with an overlay arrow for "completed" */}
            <div
                className="rwd__bar"
                role="progressbar"
                aria-label={t(unitsCompletedLabel, {
                    percent: Math.round(completedPct),
                })}
            >
                <div
                    className="rwd__seg hc8-bg-warning"
                    style={{ width: toPctStr(latePct) }}
                />
                <div
                    className="rwd__seg hc8-clr-alert"
                    style={{ width: toPctStr(carryPct) }}
                />
                <div
                    className="rwd__seg hc8-bg-inverse"
                    style={{ width: toPctStr(todayPct) }}
                />
                <div
                    className="rwd__seg hc8-bg-inactive"
                    style={{ width: toPctStr(remainingPct) }}
                />
                <div
                    className="rwd__completed hc8-clr-success"
                    style={{ width: toPctStr(completedPct), left: 0 }}
                >
                    <Tooltip
                        className="rwd__completed-tooltip"
                        content={`${Math.round(completedPct)}% complete`}
                        location={tooltipLocation}
                    >
                        <span className="rwd__completed-hit" />
                    </Tooltip>
                </div>
            </div>
            {/* Legend */}
            <div className="rwd__legend">
                <span className="lgd">
                    <span className="lgd__sw hc8-bg-warning" />
                    <span className="lgd__label hc8-clr-base">{`${t(headings.priorDayLateUnits)}: ${fmtInt(late)}`}</span>
                </span>

                <span className="lgd">
                    <span className="lgd__sw hc8-clr-alert" />
                    <span className="lgd__label hc8-clr-base">{`${t(headings.priorDayCarryoverUnits)}: ${fmtInt(carryover)}`}</span>
                </span>

                <span className="lgd">
                    <span className="lgd__sw hc8-bg-inverse" />
                    <span className="lgd__label hc8-clr-base">{`${t(headings.todaysWork)}: ${fmtInt(today)}`}</span>
                </span>

                <span className="lgd">
                    <span className="lgd__sw hc8-bg-success" />
                    <span className="lgd__label hc8-clr-base">{`${t(headings.completedUnits)}: ${fmtInt(completed)}`}</span>
                </span>
            </div>
            <div
                className={`hc8-pt-2x hc8-pb-2x ${getColorClass(sfsData?.inf?.color_indicator)}`}
            >
                {`${infPercentage} ${t(headings.percentINF)}`}
            </div>
        </div>
    )
}

// Memoized component to prevent unnecessary re-renders
export default memo(ArrowComponent)
